import { Injectable, Injector } from '@angular/core';
import { EventConfig, EventContext, ScriptAPI, SimpleCondition } from '../interfaces/event-script.models';
import { BuilderScreenBetaService } from './builder-screen.service';
import { TabControlService } from './tab-control.service';
import { BuilderProps } from '../interfaces/builder-screen.models';

/**
 * Serviço de runtime para execução de scripts de eventos
 * Fornece ambiente seguro (sandbox) e executa ações definidas pelos usuários
 */
@Injectable({ providedIn: 'root' })
export class EventScriptRuntimeService {
  
  private executionTimeout = 5000; // 5 segundos
  private executionCount = 0; // Contador para debug

  constructor(private injector: Injector) {}

  /**
   * Executa um evento configurado
   * @param event Configuração do evento
   * @param context Contexto de execução
   */
  execute(event: EventConfig, context: EventContext): void {
    if (!event.enabled) {
      return;
    }

    this.executionCount++;
    const executionId = this.executionCount;

    try {
      console.log(`[EventScript ${executionId}] Executando evento:`, event.eventType, event.description);

      if (event.mode === 'simple') {
        this.executeSimpleMode(event, context);
      } else if (event.mode === 'expert') {
        this.executeExpertMode(event, context);
      }

      console.log(`[EventScript ${executionId}] Execução concluída com sucesso`);
    } catch (error: any) {
      console.error(`[EventScript ${executionId}] Erro ao executar evento:`, error);
      // Não propagar erro para não quebrar a aplicação
    }
  }

  /**
   * Executa evento no modo simples (condições e ações predefinidas)
   */
  private executeSimpleMode(event: EventConfig, context: EventContext): void {
    // Avaliar condições
    const conditionsMet = this.evaluateConditions(event.simpleConditions || [], context);

    console.log('[EventScript] Condições atendidas:', conditionsMet);

    if (conditionsMet) {
      // Executar ações
      (event.simpleActions || []).forEach(action => {
        this.executeAction(action.type, action.targetId, action.value, context);
      });
    }
  }

  /**
   * Executa evento no modo expert (código customizado)
   */
  private executeExpertMode(event: EventConfig, context: EventContext): void {
    if (!event.customScript || !event.customScript.code) {
      console.warn('[EventScript] Script customizado vazio');
      return;
    }

    // Criar sandbox com API segura
    const api = this.createSandboxAPI(context);
    
    // Executar com timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Script timeout - execução excedeu 5 segundos')), this.executionTimeout);
    });

    const executionPromise = new Promise<void>((resolve, reject) => {
      try {
        // Criar função com escopo limitado
        // eslint-disable-next-line no-new-func
        const fn = new Function('context', 'api', event.customScript!.code);
        fn(context, api);
        resolve();
      } catch (error) {
        reject(error);
      }
    });

    Promise.race([executionPromise, timeoutPromise]).catch(error => {
      console.error('[EventScript] Erro na execução do script:', error);
    });
  }

  /**
   * Avalia se todas as condições são atendidas
   */
  private evaluateConditions(conditions: SimpleCondition[], context: EventContext): boolean {
    if (conditions.length === 0) {
      return true; // Sem condições = sempre executa
    }

    return conditions.every(condition => this.evaluateSingleCondition(condition, context));
  }

  /**
   * Avalia uma única condição
   */
  private evaluateSingleCondition(condition: SimpleCondition, context: EventContext): boolean {
    let fieldValue: any;

    // 'self' refere-se ao campo atual
    if (condition.field === 'self') {
      fieldValue = context.currentValue;
    } else {
      // Buscar valor de outro campo no formData
      fieldValue = context.formData[condition.field];
    }

    switch (condition.operator) {
      case '==':
        return fieldValue == condition.value;
      case '!=':
        return fieldValue != condition.value;
      case '>':
        return Number(fieldValue) > Number(condition.value);
      case '<':
        return Number(fieldValue) < Number(condition.value);
      case '>=':
        return Number(fieldValue) >= Number(condition.value);
      case '<=':
        return Number(fieldValue) <= Number(condition.value);
      case 'contains':
        return String(fieldValue).includes(String(condition.value));
      case 'empty':
        return !fieldValue || fieldValue === '' || fieldValue === null || fieldValue === undefined;
      case 'notEmpty':
        return !!fieldValue && fieldValue !== '';
      default:
        console.warn('[EventScript] Operador desconhecido:', condition.operator);
        return false;
    }
  }

  /**
   * Executa uma ação específica
   */
  private executeAction(type: string, targetId: string, value: any, context: EventContext): void {
    console.log('[EventScript] Executando ação:', type, 'em', targetId);

    const builderService = this.injector.get(BuilderScreenBetaService);
    const tabControlService = this.injector.get(TabControlService);

    try {
      switch (type) {
        case 'showTab':
          tabControlService.showTab(targetId);
          break;
        
        case 'hideTab':
          tabControlService.hideTab(targetId);
          break;
        
        case 'enableTab':
          tabControlService.enableTab(targetId);
          break;
        
        case 'disableTab':
          tabControlService.disableTab(targetId);
          break;
        
        case 'showComponent':
          this.updateComponentVisibility(builderService, targetId, true);
          break;
        
        case 'hideComponent':
          this.updateComponentVisibility(builderService, targetId, false);
          break;
        
        case 'enableComponent':
          this.updateComponentState(builderService, targetId, false); // disabled = false
          break;
        
        case 'disableComponent':
          this.updateComponentState(builderService, targetId, true); // disabled = true
          break;
        
        case 'setValue':
          this.setComponentValue(builderService, targetId, value);
          break;
        
        case 'clearValue':
          this.setComponentValue(builderService, targetId, null);
          break;
        
        default:
          console.warn('[EventScript] Tipo de ação desconhecido:', type);
      }
    } catch (error) {
      console.error('[EventScript] Erro ao executar ação:', error);
    }
  }

  /**
   * Atualiza visibilidade de um componente
   */
  private updateComponentVisibility(builderService: BuilderScreenBetaService, componentId: string, visible: boolean): void {
    const node = builderService.currentState.nodes.find(n => n.id === componentId);
    if (node) {
      builderService.updateNodeProps(componentId, {
        ...node.props,
        hidden: !visible
      } as any);
    }
  }

  /**
   * Atualiza estado (enabled/disabled) de um componente
   */
  private updateComponentState(builderService: BuilderScreenBetaService, componentId: string, disabled: boolean): void {
    const node = builderService.currentState.nodes.find(n => n.id === componentId);
    if (node) {
      builderService.updateNodeProps(componentId, {
        ...node.props,
        disabled
      } as any);
    }
  }

  /**
   * Define valor de um componente
   */
  private setComponentValue(builderService: BuilderScreenBetaService, componentId: string, value: any): void {
    // Nota: Esta é uma operação complexa que requer integração com o FormControl
    // Por agora, apenas registra no console
    console.log('[EventScript] setValue não totalmente implementado ainda:', componentId, value);
    // TODO: Integrar com FormControl do canvas para atualizar valores
  }

  /**
   * Cria API sandbox segura para scripts customizados
   */
  private createSandboxAPI(context: EventContext): ScriptAPI {
    const builderService = this.injector.get(BuilderScreenBetaService);
    const tabControlService = this.injector.get(TabControlService);

    return {
      // Componentes
      getComponent: (id: string) => {
        const node = builderService.currentState.nodes.find(n => n.id === id);
        return node ? { ...node } : null;
      },
      
      setComponent: (id: string, props: Partial<BuilderProps>) => {
        builderService.updateNodeProps(id, props as any);
      },
      
      setValue: (id: string, value: any) => {
        this.setComponentValue(builderService, id, value);
      },
      
      getValue: (id: string) => {
        return context.formData[id];
      },
      
      // Tabs
      showTab: (tabId: string) => {
        tabControlService.showTab(tabId);
      },
      
      hideTab: (tabId: string) => {
        tabControlService.hideTab(tabId);
      },
      
      enableTab: (tabId: string) => {
        tabControlService.enableTab(tabId);
      },
      
      disableTab: (tabId: string) => {
        tabControlService.disableTab(tabId);
      },
      
      // Dados
      getFormData: () => {
        return { ...context.formData };
      },
      
      // Contexto
      currentValue: context.currentValue,
      previousValue: context.previousValue,
      componentId: context.componentId
    };
  }

  /**
   * Obtém estatísticas de execução (para debug)
   */
  getExecutionStats(): { totalExecutions: number } {
    return {
      totalExecutions: this.executionCount
    };
  }

  /**
   * Reset contador de execuções (para testes)
   */
  resetStats(): void {
    this.executionCount = 0;
  }
}



